import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Account } from 'app/core/models';
import { Project } from 'app/modules/projects/projects.types';
import { RelatedToType } from 'app/shared/enums';
import { filter, switchMap } from 'rxjs/operators';
import { FoldersTabService } from '../../folders/folders.service';
import { FileResource } from '../../folders/folders.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';

type FileInput = { files: FileList; value: string };
type FileUploadEvent = { target: FileInput };

@Component({
  selector: 'app-files-comments-form',
  templateUrl: './files-comments-form.component.html',
  styleUrls: ['./files-comments-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilesCommentsFormComponent extends OnDestroyMixin {
  @Input() data: any;
  @Input() commentsConfig: { relatedToId: number; relatedToType: RelatedToType };
  @Input() fileResources: FileResource[];
  @Output() onFileUpdated = new EventEmitter<FileResource>();

  get account(): Account {
    return this.accountService.getCurrentAccount();
  }

  get project(): Project {
    return this.data.project;
  }

  get relatedToId(): number {
    return this.commentsConfig.relatedToId;
  }

  get relatedToType(): RelatedToType {
    return this.commentsConfig.relatedToType;
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly foldersTabService: FoldersTabService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmService,
  ) {
    super();
  }

  downloadDocument(fileResource: FileResource): void {
    if (fileResource.documents.length > 0) {
      let document = fileResource.documents[0];
      window.open(document.original_url, '_blank');
    }
  }

  deleteFile(fileResource: FileResource): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.foldersTabService.moduleId = this.project.id;
        this.foldersTabService.accountId = this.account.id;
        this.foldersTabService.moduleBaseUrl = 'projects';

        this.foldersTabService
          .deleteFile(fileResource.folder_resource_id, fileResource.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe((response: FileResource) => {
            this.onFileUpdated.emit(response);
            this.fileResources = this.fileResources.filter(({ id }) => id !== response.id);
            this.changeDetectorRef.detectChanges();
          });
      });
  }

  onFileChanged(event: Event & FileUploadEvent): void {
    const fileToUpload = event.target.files.item(0);
    const fileResource: FileResource = {
      folder_resource_id: this.project.root_folder_resource_id,
      name: fileToUpload.name,
      related_to_type: this.relatedToType,
      related_to_id: this.relatedToId,
    };

    this.foldersTabService.moduleId = this.project.id;
    this.foldersTabService.accountId = this.account.id;
    this.foldersTabService.moduleBaseUrl = 'projects';

    this.foldersTabService
      .createFile(fileResource.folder_resource_id, fileResource)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(({ folder_resource_id, id }) =>
          this.foldersTabService.uploadDocument(folder_resource_id, id, fileToUpload),
        ),
      )
      .subscribe((response: FileResource) => {
        this.onFileUpdated.emit(response);

        if (!this.fileResources) {
          this.fileResources = [];
        }

        this.fileResources = [...this.fileResources, response];
        this.changeDetectorRef.detectChanges();
      });
  }
}
